import React, { useState, useCallback } from 'react';
import { AttachableProductTileSkeleton, CircleLoader } from '@hpstellar/core';
import { Modal, Link, Typography, Button, EmblaCarousel } from '@hpstellar/core/visId';
import { CheckCircle } from '@hpstellar/vis-icons';

import { withError } from '../../../shared/components/error-boundary';
import useProductPrice from '../../../hooks/useProductPrice';
import useCartState, { useCartActions } from '../../../hooks/useCartState';
import { useBreakpoint } from '../../../hooks/useDeviceInfo';
import { formatGTMAction } from '../../../metrics/metrics-helpers';

import './gift-modal.less';

const pickReward = (rewardOptionId, rewardOption) => {
    return rewardOption.find(({ rewardOptionId: id }) => id === rewardOptionId);
};

const pickPriceById = (item, prices) => {
    const { sku, productId: id } = item || {};

    return sku ? prices[sku] : Object.values(prices).find(({ itemId }) => itemId === id);
};

const getSelectedSKUIds = (items, selections) => {
    return items.filter(({ productId }) => selections.includes(productId)).map(({ sku }) => sku);
};

const ProductCard = ({ priceData, selected, onClick, className = 'gift-product-card' }) => {
    if (!priceData) {
        return <AttachableProductTileSkeleton className={`${className} loader`} />;
    }
    const { fullImages, prodName, salePrice } = priceData || {};
    //get first key that starts with Img_Center
    const imageKey = Object.keys(fullImages || {}).find(key => key.includes('Img_Center'));
    const image = (fullImages || {})[imageKey];
    return (
        <div className={`${className}${selected ? ' selected' : ''}`} onClick={onClick}>
            <div>
                <div className="img-box">
                    <img src={image} alt={priceData?.productName} />
                    {selected && <CheckCircle className="selected-icon" />}
                </div>
                <Typography className="prd-title" variant="body1">
                    {prodName}
                </Typography>
            </div>
            <div className="prd-price-block">
                {salePrice && (
                    <Typography variant="footnotes" className="original-price">
                        Worth $<span className="strike-price">{salePrice}</span>
                    </Typography>
                )}
                <Typography variant="subtitle" weight={700}>
                    FREE
                </Typography>
            </div>
        </div>
    );
};
const HEADER_PROPS = {
    style: {
        borderBottom: 'none',
    },
};

const CONTENT_PROPS = {
    style: {
        maxHeight: '100%',
    },
};

export default withError(
    ({
        rewardOptionId,
        message,
        title = 'Choose your free gift',
        subtitle = (
            <>
                Select your free gift and proceed to cart.
                <br /> To remove it click on selected card and proceed to cart.
            </>
        ),
        modalSize = 'lg',
        buttonChildren = 'Add free gift to cart',
        headerProps = HEADER_PROPS,
        contentProps = CONTENT_PROPS,
    }) => {
        const { cartInfo } = useCartState();
        const { cartView } = cartInfo || {};
        const { rewardOption } = cartView || {};
        const reward = pickReward(rewardOptionId, rewardOption);
        const { rewardSpecMaxQuantity, rewardSpecGiftItem, rewardChoiceGiftItem } = reward || {};
        const [firstSelection] = rewardChoiceGiftItem || [];
        const [isOpen, setIsOpen] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [selections, setSelections] = useState((rewardChoiceGiftItem || []).map(({ productId }) => productId));
        const { prices } = useProductPrice(isOpen ? rewardSpecGiftItem : []);
        const { updateFreeGiftSelection } = useCartActions();
        const initialSlide = Math.max(
            0,
            rewardSpecGiftItem.findIndex(({ productId }) => productId === firstSelection?.productId),
        );
        const isMobile = useBreakpoint([0, 768]);
        const slidesToScroll = isMobile ? 1 : 3;
        const emblaOptions = {
            slidesToScroll,
        };
        const disabled =
            (selections.length === 0 && rewardChoiceGiftItem?.length < 0) ||
            selections.length > rewardSpecMaxQuantity * 1;
        const gtmValue = getSelectedSKUIds(rewardSpecGiftItem, selections);
        const gtmActions = !disabled
            ? formatGTMAction({
                  category: 'linkClick',
                  id: 'free-gift-attach',
                  value: gtmValue,
              })
            : {};
        const onCardClick = useCallback(
            id => () => {
                const index = selections.indexOf(id);
                if (index > -1) {
                    setSelections(selections.filter((_, i) => i !== index));
                } else {
                    setSelections([...selections, id]);
                }
            },
            [selections],
        );
        const onSubmit = useCallback(() => {
            setIsSubmitting(true);
            updateFreeGiftSelection(rewardOptionId, selections).then(resp => {
                setIsOpen(false);
                setIsSubmitting(false);
            });
        }, [selections]);
        return (
            <>
                <Link style={{ cursor: 'pointer' }} onClick={() => setIsOpen(true)}>
                    {message}
                </Link>
                <Modal
                    isOpen={isOpen}
                    headerProps={headerProps}
                    onClose={() => setIsOpen(false)}
                    size={modalSize}
                    verticalAlignment={isMobile ? 'bottom' : 'center'}
                    title={title}
                >
                    <div className="gift-modal">
                        {subtitle && (
                            <Typography className="modal-subtitle" variant="subtitle">
                                {subtitle}
                            </Typography>
                        )}
                        <div>
                            <EmblaCarousel
                                autoPlay={false}
                                arrows={false}
                                infinite={false}
                                slidesToShow="auto"
                                emblaOptions={emblaOptions}
                                slideClassName="gift-product-slide"
                                initialSlide={initialSlide}
                            >
                                {rewardSpecGiftItem.map(item => {
                                    const { productId } = item || {};
                                    const priceData = pickPriceById(item, prices);
                                    const selected = selections.includes(productId);
                                    return (
                                        <ProductCard
                                            key={productId}
                                            priceData={priceData}
                                            selected={selected}
                                            onClick={onCardClick(productId)}
                                        />
                                    );
                                })}
                            </EmblaCarousel>
                            {!isSubmitting ? (
                                <Button className="add-gifts" disabled={disabled} onClick={onSubmit} {...gtmActions}>
                                    {buttonChildren}
                                </Button>
                            ) : (
                                <div className="add-gifts loader">
                                    <CircleLoader />
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            </>
        );
    },
);
